import { TagKey } from '@cof/tag-catalog-refi-user-entry-mfe';
import { CODELESS_ENTRY_CONST } from '../../constants/constants.js';
import { DebugService } from '../debug/debug.service.js';
import { stratum } from '../../constants/stratum.js';

export function setCodelessEntryV1Flow(flag) {
  sessionStorage.setItem(CODELESS_ENTRY_CONST.SESSION_STORAGE.V1_FLOW_KEY, String(flag));
}
export function getCodelessEntryV1Flow() {
  return sessionStorage.getItem(CODELESS_ENTRY_CONST.SESSION_STORAGE.V1_FLOW_KEY);
}
export function clearCodelessEntryV1Flow() {
  sessionStorage.removeItem(CODELESS_ENTRY_CONST.SESSION_STORAGE.V1_FLOW_KEY);
}

export function setCodelessEntryFlowViewed(flag) {
  sessionStorage.setItem(CODELESS_ENTRY_CONST.SESSION_STORAGE.FLOW_VIEWED_KEY, String(flag));
}
export function clearCodelessEntryFlowViewed() {
  sessionStorage.removeItem(CODELESS_ENTRY_CONST.SESSION_STORAGE.FLOW_VIEWED_KEY);
}
export function getCodelessEntryFlowViewed() {
  return sessionStorage.getItem(CODELESS_ENTRY_CONST.SESSION_STORAGE.FLOW_VIEWED_KEY);
}

export function isCodelessEntryEligibleAndNotRedirected() {
  return getCodelessEntryV1Flow() === 'true' && !getCodelessEntryFlowViewed();
}

/**
 * Service for handling codeless entry routing logic
 * Manages allocation-based routing for grlpv1 flow
 */
class _CodelessEntryRoutingService {
  constructor() {
    this.debug = new DebugService('CodelessEntryRoutingService');
  }

  /**
   * Handles allocation-based routing for codeless entry V1 flow
   * Control flow does not need session storage as it's handled directly on offer page
   * @param {Object} parsedReturnCust - Parsed return customer data
   * @param {Function} isFeatureEnabled - Feature toggle checker function
   * @returns {boolean} - True if routing was handled, false if no routing occurred
   */
  handleCodelessEntryRouting(parsedReturnCust, isFeatureEnabled) {
    // Validate inputs
    if (!parsedReturnCust || typeof isFeatureEnabled !== 'function') {
      this.debug.log('handleCodelessEntryRouting: invalid input parameters');
      stratum.publishTag(TagKey.CODELESS_ENTRY_NOT_ELIGIBLE, {
        replacements: { value: JSON.stringify({ parsedReturnCust, message: 'handleCodelessEntryRouting: invalid input parameters' }) },
      });
      return false;
    }

    if (!isFeatureEnabled(CODELESS_ENTRY_CONST.FEATURE_TOGGLE)) {
      this.debug.log('handleCodelessEntryRouting: codeless entry feature disabled');
      stratum.publishTag(TagKey.CODELESS_ENTRY_NOT_ELIGIBLE, {
        replacements: { value: JSON.stringify({ parsedReturnCust, isFeatureEnabled: false, message: 'handleCodelessEntryRouting: codeless entry feature disabled' }) },
      });
      return false;
    }

    // Handle grlpv1 allocation
    if (parsedReturnCust.isCodelessEntryV1) {
      setCodelessEntryV1Flow(true);
      clearCodelessEntryFlowViewed();
      this.debug.log('handleCodelessEntryRouting: V1 session flag set');
      stratum.publishTag(TagKey.CODELESS_ENTRY_ELIGIBLE, {
        replacements: { value: JSON.stringify(parsedReturnCust) },
      });
      return true;
    }

    this.debug.log('handleCodelessEntryRouting: no codeless entry allocation matched');
    stratum.publishTag(TagKey.CODELESS_ENTRY_NOT_ELIGIBLE, {
      replacements: { value: JSON.stringify({ parsedReturnCust, message: 'handleCodelessEntryRouting: no codeless entry allocation matched' }) },
    });
    return false;
  }

  /**
   * Checks if codeless entry routing is applicable for the given parsed data
   * @param {Object} parsedReturnCust - Parsed return customer data
   * @param {Function} isFeatureEnabled - Feature toggle checker function
   * @returns {boolean} - True if codeless entry routing can be applied
   */
  canHandleRouting(parsedReturnCust, isFeatureEnabled) {
    if (!parsedReturnCust || typeof isFeatureEnabled !== 'function') {
      return false;
    }

    return isFeatureEnabled(CODELESS_ENTRY_CONST.FEATURE_TOGGLE) && parsedReturnCust.isCodelessEntryV1;
  }
}

export const CodelessEntryRoutingService = new _CodelessEntryRoutingService();
